import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DataTableMultiSelectFilterOption } from '@shared/components/data-table-multi-select-filter/data-table-multi-select-filter.component';

@Component({
  selector: 'app-spiritual-assistance-visit-responsible-priest-filter',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatInputModule],
  templateUrl: './spiritual-assistance-visit-responsible-priest-filter.component.html',
  styleUrl: './spiritual-assistance-visit-responsible-priest-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SpiritualAssistanceVisitResponsiblePriestFilterComponent), multi: true }],
})
export class SpiritualAssistanceVisitResponsiblePriestFilterComponent implements ControlValueAccessor, OnChanges {
  @Input() pastors: DataTableMultiSelectFilterOption[] = [];
  @Input() allSelectionLabel = '';

  readonly search = new FormControl('', { nonNullable: true });
  panelOpen = false;
  private selectedIds: string[] = [];
  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pastors']) this.cdr.markForCheck();
  }

  writeValue(value: string[] | null): void {
    this.selectedIds = [...new Set((value ?? []).filter(Boolean))];
    this.cdr.markForCheck();
  }

  registerOnChange(onChange: (value: string[]) => void): void { this.onChange = onChange; }
  registerOnTouched(onTouched: () => void): void { this.onTouched = onTouched; }

  togglePanel(): void { this.panelOpen = !this.panelOpen; this.cdr.markForCheck(); }


  toggle(personId: string, selected: boolean): void {
    this.selectedIds = selected
      ? [...new Set([...this.selectedIds, personId])]
      : this.selectedIds.filter(id => id !== personId);
    this.onChange(this.selectedIds);
    this.onTouched();
    this.cdr.markForCheck();
  }

  isSelected(personId: string): boolean { return this.selectedIds.includes(personId); }

  get selectedPastors(): DataTableMultiSelectFilterOption[] {
    return this.pastors.filter(pastor => this.selectedIds.includes(pastor.value));
  }

  get selectionLabel(): string {
    if (this.allSelectionLabel && this.selectedIds.length === this.pastors.length) {
      return this.allSelectionLabel;
    }

    return this.selectedPastors.map(pastor => pastor.label ?? pastor.value).join(', ');
  }

  get filteredPastors(): DataTableMultiSelectFilterOption[] {
    const term = this.normalizeText(this.search.value);
    const pastors = term ? this.pastors.filter(pastor => this.normalizeText(pastor.label ?? pastor.value).includes(term)) : this.pastors;
    return [...pastors].sort((first, second) => Number(this.isSelected(second.value)) - Number(this.isSelected(first.value)));
  }

  private normalizeText(value: string): string { return value.toLocaleLowerCase('es').normalize('NFD').replace(/[\u0300-\u036f]/g, ''); }
}
